import { useEffect, useRef } from 'react';
// Two decimal places, matching what the score inputs accept (step 0.01) and
// what both quality tables already render (toFixed(2)). The raw mean of three
// integers is frequently unrepresentable — 10/3 is 3.3333333333333335 — which
// would both fail the input's step validation and read as noise in the form.
const SCALE = 100;
/**
 * Overall = the mean of the per-area ratings that carry a number.
 *
 * matt's rule, revised 2026-08-27 23:36 on SAD-154 and SAD-161 alike: "The
 * 'Overall' value should just be an average of the values, decimal places are
 * ok, so no need to round." An earlier reading of the same rule rounded to the
 * nearest whole number; this is the correction, and it applies to both quality
 * surfaces because matt asked for it on both tickets in the same minute.
 *
 * An area the source document rated N/A reaches the form as `undefined`, not
 * 0 (ProposalReview unwraps a nullValue to undefined), so it is excluded here
 * simply by never being a number — no special case, and no way for an N/A to
 * drag the mean down. By the same token a form with no rated areas at all
 * derives nothing rather than 0, which is what keeps the NULL-persists-as-an
 * em-dash behaviour PR #995 shipped: derived-from-real-ratings and
 * coerced-to-zero are different things, and only the first is wanted.
 */ export function deriveOverallScore(categories) {
    const rated = categories.filter((v)=>typeof v === 'number' && Number.isFinite(v));
    if (rated.length === 0) return undefined;
    return Math.round(rated.reduce((sum, v)=>sum + v, 0) / rated.length * SCALE) / SCALE;
}
/**
 * Keeps the Overall field in step with the per-area ratings, and stops the
 * moment the reviewer takes the field over.
 *
 * Ownership is tracked explicitly — the value this component last wrote, plus
 * whether it still owns the field — rather than inferred by comparing Overall
 * against the last derived number. That comparison cannot tell "our derivation"
 * from "a number the reviewer typed" once the two coincide, so a reviewer who
 * cleared Overall and re-entered a value this component had previously derived
 * used to lose it silently at the next rating change. Watching the Overall
 * field itself, instead of only the ratings, is what makes the hand-off
 * observable.
 *
 * The one case no value-level check can catch is typing a number identical to
 * the one already sitting there: `watch` reports values, not provenance, so
 * that edit is invisible. It is also a no-op — the field keeps the number the
 * reviewer wanted — and distinguishing it would mean reaching into
 * react-hook-form's touched state, which is exactly the coupling
 * OverallDerivationFormAccess exists to avoid.
 *
 * Ownership is released by ANY change this component did not make: a typed
 * value, an Apply that copies an accepted Overall proposal in, or the reviewer
 * clearing the field. Clearing deliberately does NOT refill — a blank Overall
 * is a storable NULL that renders as an em dash (PR #995), so re-deriving into
 * it would make a rated record's Overall impossible to leave empty. Once
 * released, ownership is never reclaimed for the life of the form.
 *
 * Renders nothing — it exists for the effect, and must live inside the form's
 * extraContent so `watch` subscribes it to live changes. Mount it on the
 * CREATE form only: an existing record's Overall is a stored value a reviewer
 * may have set deliberately, and re-deriving it on edit would rewrite history
 * rather than fill a blank.
 */ export function OverallDerivation({ form, categoryFields, overallField }) {
    const derived = deriveOverallScore(categoryFields.map((name)=>form.watch(name)));
    // Subscribed deliberately: the component has to see Overall change to know
    // it no longer owns the field.
    const current = form.watch(overallField);
    // The access object is rebuilt on every EntityForm render; refs keep it and
    // the field name out of the dependency list so the effect runs on real value
    // changes only.
    const formRef = useRef(form);
    formRef.current = form;
    const fieldRef = useRef(overallField);
    fieldRef.current = overallField;
    const written = useRef(undefined);
    const owns = useRef(true);
    useEffect(()=>{
        if (owns.current && current !== written.current) owns.current = false;
        if (!owns.current) return;
        if (current !== derived) {
            formRef.current.setValue(fieldRef.current, derived);
            written.current = derived;
        }
    }, [
        current,
        derived
    ]);
    return null;
}
